<template>
    <div>
        <!-- Student's Own Internship Applications Table -->
        <div v-if="checkPermission('coopinternshipapplication_me')" class="mb-5">
            <h4 class="mb-3 font-weight-bold">{{ $t('coop_my_applications') }}</h4>
            <datatable ref="myApplicationsTable"
                       :isLoading.sync="myApplicationsTable.isLoading"
                       :columns="myApplicationsTable.columns"
                       :rows="myApplicationsTable.rows"
                       :total="myApplicationsTable.total"
                       :queryParams="myApplicationsTable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onMyApplicationsPageChange"
                       @on-sort-change="onMyApplicationsSortChange"
                       @on-per-page-change="onMyApplicationsPerPageChange"
                       v-show="myApplicationsTable.showTable"
            />
        </div>

        <!-- Active Internship Postings Table -->
        <div>
            <h4 class="mb-3 font-weight-bold">{{ $t('coop_internship_postings') }}</h4>
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />
        </div>

        <!-- Document Upload Modal -->
        <CommonModal ref="documentsModal" size="lg" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_documents') }}
            </template>
            <template v-slot:CommonModalContent>
                <div v-if="selectedApplication">
                    <div v-for="(doc, idx) in documents" :key="idx" class="d-flex align-items-center mb-3 pb-2 border-bottom">
                        <div class="mr-3 font-weight-bold" style="width: 140px">{{ doc.type || doc.label || doc.code }}</div>
                        <div class="flex-grow-1 mr-2">
                            <b-form-file size="sm"
                                          v-model="doc.file"
                                          :placeholder="$t('choose_file')"
                                          drop-placeholder="$t('drop_file')" />
                        </div>
                        <b-button size="sm" variant="outline-primary" class="mr-2" :disabled="!doc.file" @click="uploadSingleDocument(doc)">
                            <i class="ri-upload-2-line mr-1"></i> {{ $t('upload') }}
                        </b-button>
                        <b-badge :variant="statusVariant(doc.status)" class="mr-2">{{ doc.status ? $t(doc.status) : $t('pending') }}</b-badge>
                    </div>
                    <div class="d-flex justify-content-end mt-3">
                        <b-button variant="success" @click="submitDocuments">
                            <i class="ri-send-plane-line mr-1"></i> {{ $t('coop_submit_documents') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';

    //Service
    import CoopAdService from '@/services/CoopAdService';
    import CoopApplicationService from '@/services/CoopApplicationService';

    //Other
    import qs from 'qs';

    export default {
        name: 'InternshipPostings',
        components: {
            DatatableFilter,
            Datatable,
            CommonModal
        },
        data() {
            return {
                selectedApplication: null,
                documents: [],
                myApplicationsTable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_upload_document'),
                                    class: 'ri-upload-cloud-2-line text-info',
                                    show: (row) => row.status === 'waiting_student_documents',
                                    callback: (row) => {
                                        this.showDocuments(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_position'),
                            field: 'position',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_posting_responsible'),
                            field: 'responsible_staff_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: this.getLocaleField('status_name'),
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                },
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_posting_detail_apply'),
                                    class: 'ri-file-text-line',
                                    callback: (row) => {
                                        this.$router.push('/coop/student/postings/' + row.id);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_position'),
                            field: 'position',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_posting_responsible'),
                            field: 'responsible_staff_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_location'),
                            field: this.getLocaleField('location_type_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_quota'),
                            field: 'quota',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_last_application_date'),
                            field: 'end_date',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getMyApplications();
            this.getRows();
        },
        methods: {
            getMyApplications() {
                if (!this.checkPermission('coopinternshipapplication_me')) {
                    return;
                }
                this.myApplicationsTable.isLoading = true;
                const config = {
                    params: { ...this.myApplicationsTable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopApplicationService.getMe(config)
                    .then((response) => {
                        this.myApplicationsTable.rows = response.data.data;
                        this.myApplicationsTable.total = response.data.pagination?.total || response.data.data.length;
                        this.myApplicationsTable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.myApplicationsTable.isLoading = false;
                    });
            },
            onMyApplicationsPageChange(params) {
                this.myApplicationsTable.queryParams.page = params.currentPage;
                this.getMyApplications();
            },
            onMyApplicationsPerPageChange(params) {
                this.myApplicationsTable.queryParams.limit = params.currentPerPage;
                this.myApplicationsTable.queryParams.page = 1;
                this.getMyApplications();
            },
            onMyApplicationsSortChange(params) {
                if (params && params.length > 0) {
                    const sortType = params[0].type === 'desc' ? '-' : '';
                    this.myApplicationsTable.queryParams.sort = sortType + params[0].field;
                }
                this.getMyApplications();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopAdService.getActive(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    const sortType = params[0].type === 'desc' ? '-' : '';
                    this.datatable.queryParams.sort = sortType + params[0].field;
                }
                this.getRows();
            },
            statusVariant(status) {
                if (status === 'approved' || status === 'accepted') return 'success';
                if (status === 'rejected') return 'danger';
                if (status === 'uploaded') return 'info';
                return 'warning';
            },
            showDocuments(row) {
                this.selectedApplication = row;
                const docList = row.documents || row.required_documents || [];
                this.documents = docList.map((doc) => {
                    if (typeof doc === 'string') {
                        return { type: doc, status: null, file: null };
                    }
                    return { ...doc, file: null };
                });
                this.$refs.documentsModal.$refs.commonModal.show();
            },
            uploadSingleDocument(doc) {
                if (!doc.file || !this.selectedApplication) return;

                const formData = new FormData();
                formData.append('type', doc.type || doc.code);
                formData.append('file', doc.file);

                CoopApplicationService.uploadDocument(this.selectedApplication.id, formData)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        doc.status = 'uploaded';
                        doc.file = null;
                        this.getMyApplications();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            submitDocuments() {
                if (!this.selectedApplication) return;
                CoopApplicationService.submitDocuments(this.selectedApplication.id)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$refs.documentsModal.$refs.commonModal.hide();
                        this.getMyApplications();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            }
        }
    };
</script>
